import { Box } from '@mui/material';

import Accordion from '@mui/material/Accordion';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionDetails, AccordionSummary, styled } from '@mui/material';
import {
  LpFeeCalculationBasis,
  LpFeeCommitmentStatus,
} from 'further-types/lp/lp-fee-template';
import Table from 'further-ui/components/Table';
import {
  LpFeeCalculationBasisLabels,
  LpFeeCommitmentStatusLabels,
} from 'further-ui/labels';
import {
  CalendarDay,
  CalendarTime,
  numberToCurrencyString,
} from 'further-ui/utils';
import { round } from 'lodash';

const StyledAccordion = styled(Accordion)({
  width: '100%',
});

const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  fontSize: theme.fontSizes.regular,
  fontWeight: theme.typography.fontWeightBold,
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const AccordionBody = styled(AccordionDetails)({
  backgroundColor: 'white',
  padding: 0,
  border: 'none',
});

type FeeDetailsForCapitalAction = {
  _id: string;
  feeActionId: string;
  limitedPartner: string;
  feeActionCreatedAt: CalendarTime;
  accrualDate: CalendarDay;
  lpFeeTemplateName: string;
  lpFeeTemplateFeeName: string;
  insideOutsideCommitment: LpFeeCommitmentStatus;
  calculationBasis: LpFeeCalculationBasis;
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  feeIncTaxLcy: number;
};

type Props = {
  fees: Array<FeeDetailsForCapitalAction>;
};

const columns = [
  {
    label: 'Limited partner',
    key: 'limitedPartner',
  },
  {
    label: 'Accrual date',
    key: 'accrualDate',
  },
  {
    label: 'Fee template name',
    key: 'lpFeeTemplateName',
  },
  {
    label: 'Fee name',
    key: 'lpFeeTemplateFeeName',
  },
  {
    label: 'Inside/outside commitment',
    key: 'insideOutsideCommitment',
  },
  {
    label: 'Calculation basis',
    key: 'calculationBasis',
  },
  {
    label: 'Fee',
    key: 'feeExTaxLcy',
  },
  {
    label: 'Sales tax',
    key: 'salesTaxAmountLcy',
  },
  {
    label: 'Total charge',
    key: 'feeIncTaxLcy',
  },
];

const FeesPreviewAccordion: React.FC<Props> = ({ fees }) => {
  const rows = fees.map((fee) => ({
    id: fee._id,
    limitedPartner: fee.limitedPartner,
    accrualDate: fee.accrualDate.toLabel(),
    lpFeeTemplateName: fee.lpFeeTemplateName,
    lpFeeTemplateFeeName: fee.lpFeeTemplateFeeName,
    insideOutsideCommitment: LpFeeCommitmentStatusLabels.get(
      fee.insideOutsideCommitment,
    ),
    calculationBasis: LpFeeCalculationBasisLabels.get(fee.calculationBasis),
    feeExTaxLcy: numberToCurrencyString(fee.feeExTaxLcy),
    salesTaxAmountLcy: numberToCurrencyString(fee.salesTaxAmountLcy),
    feeIncTaxLcy: numberToCurrencyString(
      round(fee.feeExTaxLcy + fee.salesTaxAmountLcy, 2),
    ),
  }));

  return (
    <Box>
      <StyledAccordion defaultExpanded>
        <AccordionHeader expandIcon={<ExpandMoreIcon color="primary" />}>
          View fees allocation data
        </AccordionHeader>
        <AccordionBody>
          <Table rows={rows} columns={columns} />
        </AccordionBody>
      </StyledAccordion>
    </Box>
  );
};

export default FeesPreviewAccordion;
